import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { ExpandMore, Gavel } from "@mui/icons-material";
import {
  DEFAULT_QUOTE_LINK_PRIVACY_POLICY,
  DEFAULT_QUOTE_LINK_TERMS,
} from "@/features/termsManagement/utils/quoteLinkTermsDefaults";

/**
 * TermsAndConditions - Displays terms and conditions with acceptance checkbox
 * Mobile-first responsive design with expandable terms
 */
export const TermsAndConditions = ({
  accepted,
  onAcceptChange,
  disabled = false,
  terms: termsProp,
  privacyPolicy: privacyPolicyProp,
  contactEmail = "info@bubakii.co.za",
  callNumber,
}) => {
  // Call number from environment (fallback to provided number)
  const CALL_NUMBER =
    callNumber || import.meta.env.VITE_CALL_NUMBER || "0676744632";
  const normalizedDigits = CALL_NUMBER.replace(/\D/g, "");
  const e164Digits = normalizedDigits.startsWith("0")
    ? `27${normalizedDigits.slice(1)}`
    : normalizedDigits.startsWith("27")
      ? normalizedDigits
      : normalizedDigits.startsWith("+27")
        ? normalizedDigits.slice(1)
        : normalizedDigits;
  const telHref = `tel:+${e164Digits}`;

  const terms = Array.isArray(termsProp) ? termsProp : DEFAULT_QUOTE_LINK_TERMS;
  const privacyPolicy = Array.isArray(privacyPolicyProp)
    ? privacyPolicyProp
    : DEFAULT_QUOTE_LINK_PRIVACY_POLICY;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Gavel color="primary" />
          <Typography variant="h6">Terms & Conditions</Typography>
        </Box>

        {/* Terms Acceptance */}
        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={(e) => onAcceptChange(e.target.checked)}
              disabled={disabled}
              color="primary"
              sx={{ alignSelf: "flex-start", mt: 0.5 }}
            />
          }
          label={
            <Typography
              variant="body2"
              color={disabled ? "text.disabled" : "text.primary"}
            >
              I have read and agree to the terms and conditions and privacy
              policy
            </Typography>
          }
          sx={{ alignItems: "flex-start", mb: 2 }}
        />

        {/* Expandable Terms */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">
              Service Terms & Conditions
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {terms.map((term, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemText
                    primary={term}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Privacy Policy */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle2">Privacy Policy</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List dense>
              {privacyPolicy.map((policy, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemText
                    primary={policy}
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Important Notice */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "warning.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "warning.200",
          }}
        >
          <Typography variant="body2" color="warning.main" fontWeight="medium">
            Important Notice:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            By accepting this quote, you agree to all terms and conditions.
            Please ensure you have read and understood all terms before
            proceeding.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Questions about these terms? Contact us at{" "}
            <Typography
              component="span"
              variant="body2"
              color="primary.main"
              sx={{ textDecoration: "underline", cursor: "pointer" }}
              onClick={() => window.open(telHref)}
            >
              {CALL_NUMBER}
            </Typography>{" "}
            or{" "}
            <Typography
              component="span"
              variant="body2"
              color="primary.main"
              sx={{ textDecoration: "underline", cursor: "pointer" }}
              onClick={() => window.open(`mailto:${contactEmail}`)}
            >
              {contactEmail}
            </Typography>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
